import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Link,
  Stack,
  Text,
  useToast,
  FormErrorMessage,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { useAuth } from '../../context/AuthContext';

/**
 * ForgotPassword page component
 * Handles password reset request with email
 */
const ForgotPassword = () => {
  const toast = useToast();
  const { forgotPassword } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // React Hook Form setup
  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm();

  // Handle form submission
  const onSubmit = async (data) => {
    try {
      setIsLoading(true);
      
      // Call forgotPassword function from auth context
      await forgotPassword(data.email);
      
      // Show success message
      setIsSubmitted(true);
    } catch (error) {
      // Show error message
      toast({
        title: 'فشل طلب إعادة تعيين كلمة المرور',
        description: error.response?.data?.message || 'حدث خطأ أثناء معالجة طلبك. يرجى المحاولة مرة أخرى.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxW="lg" py={{ base: '12', md: '24' }} px={{ base: '0', sm: '8' }}>
      <Stack spacing="8">
        <Stack spacing="6" textAlign="center">
          <Heading size="xl" fontWeight="bold">
            نسيت كلمة المرور
          </Heading>
          <Text color="gray.500">
            أدخل بريدك الإلكتروني وسنرسل لك رابطًا لإعادة تعيين كلمة المرور
          </Text>
        </Stack>
        <Box
          py={{ base: '0', sm: '8' }}
          px={{ base: '4', sm: '10' }}
          bg={{ base: 'transparent', sm: 'white' }}
          boxShadow={{ base: 'none', sm: 'md' }}
          borderRadius={{ base: 'none', sm: 'xl' }}
        >
          {isSubmitted ? (
            <Alert
              status="success"
              variant="subtle"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              textAlign="center"
              height="200px"
              borderRadius="md"
            >
              <AlertIcon boxSize="40px" mr={0} />
              <AlertTitle mt={4} mb={1} fontSize="lg">
                تم إرسال بريد إلكتروني لإعادة تعيين كلمة المرور
              </AlertTitle>
              <AlertDescription maxWidth="sm">
                لقد أرسلنا بريدًا إلكترونيًا يحتوي على رابط لإعادة تعيين كلمة المرور الخاصة بك. يرجى التحقق من بريدك الإلكتروني.
              </AlertDescription>
              <Link as={RouterLink} to="/login" color="primary.500" fontWeight="semibold" mt={4}>
                العودة إلى صفحة تسجيل الدخول
              </Link>
            </Alert>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)}>
              <Stack spacing="6">
                <FormControl isInvalid={errors.email}>
                  <FormLabel htmlFor="email">البريد الإلكتروني</FormLabel>
                  <Input
                    id="email"
                    type="email"
                    {...register('email', {
                      required: 'البريد الإلكتروني مطلوب',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'عنوان البريد الإلكتروني غير صالح',
                      },
                    })}
                  />
                  <FormErrorMessage>
                    {errors.email && errors.email.message}
                  </FormErrorMessage>
                </FormControl>

                <Stack spacing="6">
                  <Button
                    type="submit"
                    colorScheme="blue"
                    variant="solid"
                    isLoading={isLoading}
                    loadingText="جاري المعالجة..."
                  >
                    إرسال رابط إعادة تعيين كلمة المرور
                  </Button>

                  <Link as={RouterLink} to="/login" color="primary.500" fontWeight="semibold" textAlign="center">
                    العودة إلى صفحة تسجيل الدخول
                  </Link>
                </Stack>
              </Stack>
            </form>
          )}
        </Box>
      </Stack>
    </Container>
  );
};

export default ForgotPassword;
